<script setup>
import { ref, computed, onMounted, watch, nextTick, inject, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import EnNavigationDrawer from 'Components/navigation/EnNavigationDrawer';
import EnNavbar from 'Components/navigation/EnNavbar';
import { useUserSettingsStore } from 'Js/store/useUserSettingsStore';

const { state } = storeToRefs(useUserSettingsStore());

const globalStore = computed(() => state?.value);

const loaded = computed(() => !!globalStore.value?.EngineUrl || !!globalStore?.value?.IsAuthUrl);

const navbarTemplate = ref();
const buttonReplacer = ref();
const router = useRouter();

const isAuthUrl = computed(() => {
  const authHost = tryCreateUrl(globalStore.value?.AuthUrl)?.host;
  return authHost ? authHost === window.location.host : globalStore?.value?.IsAuthUrl ?? false;
});
const isLoggedIn = computed(() => !!globalStore?.value?.UserId);
const userIsGlobalAdmin = computed(() => globalStore?.value?.IsGlobalAdmin ?? false);
/**
 * If value exists, return url. Otherwise return null
 * @param url
 */
function tryCreateUrl(url) {
  if (url) {
    return new URL(url);
  }
  return null;
}

const rail = ref(false);
const expandedAreas = ref([]);
const isProdViewEnabled = ref(false);

const searchQuery = ref('');

function updateRail(value) {
  rail.value = value;
  localStorage.setItem('sidebarState', JSON.stringify(rail.value));

  if (value) {
    expandedAreas.value = [];
  }
}

provide('updateRail', updateRail);

function handleSearchUpdate(newValue) {
  searchQuery.value = newValue;
}

onMounted(async () => {
  const savedSidebarState = localStorage.getItem('sidebarState');
  if (savedSidebarState !== null) {
    rail.value = JSON.parse(savedSidebarState);
  }
});
</script>

<template>
  <div class="en-app-layout">
    <en-navigation-drawer
        v-model:rail="rail"
        :show-user-imitation-menu-item="userIsGlobalAdmin"
        :is-prod-view-enabled="isProdViewEnabled"
        :is-auth-url="isAuthUrl"
        :is-logged-in="isLoggedIn"
        @update:rail="updateRail"
        @toggle-prod-view="isProdViewEnabled = !isProdViewEnabled"
    ></en-navigation-drawer>
    <div class="en-app-layout__main">
      <div class="en-navbar-wrapper" ref="navbarTemplate">
        <en-navbar
            v-if="loaded"
            :class="{ 'expanded': !rail }"
            :search-query="searchQuery"
            :is-prod-view-enabled="isProdViewEnabled"
            :is-auth-url="isAuthUrl"
            :is-logged-in="isLoggedIn"
            :engine-url="globalStore?.EngineUrl"
            @update:searchQuery="handleSearchUpdate"
        >
          <template v-for="(slot, name) in $slots" :key="name" v-slot:[name]="slotProps">
            <slot :name="name" v-bind="slotProps"></slot>
          </template>
        </en-navbar>
      </div>
      <div class="en-app-layout__page">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables.scss";
@use "Styles/helper/mixins.scss";

/*
 * The sidebar and the main column sit next to each other, so the sidebar pushes the page aside as it opens or collapses.
 * Navbar and page share the same side padding, so the breadcrumbs line up with the page content.
 * Set --en-page-max-width to limit the width of the page, it then stays centered.
 * Sidebar and navbar are fixed to the viewport, so only the page bounces on overscroll (macOS).
 */
.en-app-layout {
  --en-page-gutter: #{variables.$spacing-s};
  --en-page-gutter-inline: var(--en-page-gutter);
  --en-page-inline-padding: max(var(--en-page-gutter-inline), calc((100% - var(--en-page-max-width, 100000px)) / 2));
  --en-drawer-width: 0px;

  display: flex;
  min-height: 100vh;

  &:has(> .en-drawer-outer) {
    --en-drawer-width: 312px;
  }

  &:has(> .en-drawer-outer.is-rail) {
    --en-drawer-width: #{variables.$navigation-rail-width};
  }

  @include mixins.media-desktop() {
    --en-page-gutter: #{variables.$spacing-m};
    --en-page-gutter-inline: 48px;
  }
}

.en-app-layout__main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  padding-top: variables.$navigation-bar-height;
}

.en-app-layout__page {
  flex: 1;
  padding: var(--en-page-gutter) var(--en-page-inline-padding);
}

/* Collapsed context menu overlay */
.context-menu {
  :deep(.v-overlay__content) {
    border-radius: variables.$context-menu-border-radius;
    border: 1px solid variables.$context-menu-border-color;
    background: variables.$en-0;
    box-shadow: 0 6px 15.3px 0 rgba(13, 48, 72, .09);
  }
}


:deep(.v-overlay__content > .v-list) {
  box-shadow: none;
}

:deep(.v-overlay__content > .v-list > .v-list-item) {
  padding: 0;
  padding-inline: 0;
  min-height: 0;
}

:deep(.v-list-item .v-list-item__prepend) {
  margin-right: 0 !important;
}

#toggler {
  button {
    background: variables.$en-0;
    border-radius: 3px;
    padding: 2px;
  }

  .toggler-icon {
    color: variables.$en-blue;
  }
}

.en-navbar-wrapper {
  position: fixed;
  top: 0;
  left: var(--en-drawer-width);
  right: 0;
  background: variables.$en-blue-2;
  z-index: 1000;
  transition: left 300ms ease;
}

</style>
<style lang="scss">
@use 'sass:math';
@use "Styles/helper/variables.scss";
@use "Styles/helper/mixins.scss";

#header-breadcrumb {
  list-style: none;
  padding-left: 0;
  margin-right: 0;
  margin-bottom: 0;
  margin-left: variables.$navigation-rail-width + 10px;
  transition: all .2s ease, margin .3s ease;
  color: variables.$en-0;

  @include mixins.ellipsis();

  li {
    display: inline-block;
    font-family: var(--en-font-primary);
    padding-right: 8px;
    font-size: 16px;
    font-weight: 500;

    a {
      color: variables.$en-blue-7;
      text-decoration: none;
      display: inline-block;
      height: 100%;

      &:hover {
        text-decoration: none;
      }
    }

    // Arrow right
    &::after {
      content: "";
      border: solid variables.$en-blue-7;
      border-width: 0 1.13px 1.13px 0;
      display: inline-block;
      padding: 4px;
      margin-left: 3px;

      @include mixins.transition(transform .3s linear);
      @include mixins.transform(rotate(-45deg));
    }

    &:nth-last-of-type(1) {
      &::after {
        border-color: white;
      }
    }

    &:last-of-type {
      a {
        color: variables.$en-0;
      }

      &::after {
        display: none;
      }
    }

  }
}

.expanded {
  #header-breadcrumb {
    margin-left: 10px;
  }
}

.auth {
  #header-breadcrumb {
    margin-left: 10px;
  }
}

// The layout above sets all spacing around the page.
main.main-v2 {
  margin: 0;
  padding: 0;
}

body.main-v2 {
  padding-top: 0;
}
</style>